// ── Compass Widget ─────────────────────────────────────────────────
// North indicator overlay for the planner canvas.
// Fixed position (top-right), unaffected by pan/zoom.
// Supports rotation to set custom North direction.

import { t } from '../shared/i18n.ts';

let compassEl: HTMLElement | null = null;
let rotation = 0; // degrees clockwise from default North (up)
let isDragging = false;
let dragStartAngle = 0;

export function getCompassRotation() {
  return rotation;
}

// @ts-expect-error — TS strict
export function setCompassRotation(deg) {
  rotation = ((deg % 360) + 360) % 360;
  updateCompassTransform();
}

export function resetCompassRotation() {
  setCompassRotation(0);
}

function updateCompassTransform() {
  if (!compassEl) return;
  const needle = compassEl!.querySelector('.compass-needle');
  if (needle) {
    // dynamic: rotation angle is runtime-computed from user drag interaction
    // @ts-expect-error — TS strict
    needle.style.transform = `rotate(${rotation}deg)`;
  }
}

function getAngleFromCenter(el: HTMLElement, clientX: number, clientY: number) {
  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  return Math.atan2(clientX - cx, -(clientY - cy)) * (180 / Math.PI);
}

function onPointerDown(e: Event) {
  isDragging = true;
  // @ts-expect-error — TS strict
  dragStartAngle = getAngleFromCenter(compassEl, (e as PointerEvent).clientX, (e as PointerEvent).clientY) - rotation;
  compassEl!.setPointerCapture((e as PointerEvent).pointerId);
  compassEl!.classList.add('compass-dragging');
  e.preventDefault();
}

function onPointerMove(e: Event) {
  if (!isDragging) return;
  // @ts-expect-error — TS strict
  const currentAngle = getAngleFromCenter(compassEl, (e as PointerEvent).clientX, (e as PointerEvent).clientY);
  setCompassRotation(currentAngle - dragStartAngle);
}

function onPointerUp(e: Event) {
  if (!isDragging) return;
  isDragging = false;
  compassEl!.releasePointerCapture((e as PointerEvent).pointerId);
  compassEl!.classList.remove('compass-dragging');
}

function onDoubleClick(e: MouseEvent) {
  e.preventDefault();
  resetCompassRotation();
}

export function createCompass(container: HTMLElement) {
  if (compassEl) return compassEl;

  compassEl = document.createElement('div');
  compassEl!.className = 'compass-widget';
  compassEl!.setAttribute('role', 'img');
  compassEl!.setAttribute('aria-label', t('compass.ariaLabel'));
  compassEl!.setAttribute('title', t('compass.tooltip'));
  // The planner builds the compass before the locale has loaded, when t()
  // still returns bare keys. The bindings let applyTranslations() fix both
  // attributes once it arrives, as data-i18n already does for the letters.
  compassEl!.dataset.i18nAriaLabel = 'compass.ariaLabel';
  compassEl!.dataset.i18nTitle = 'compass.tooltip';
  compassEl!.dataset.testid = 'compass-widget';

  const needleWrap = document.createElement('div');
  needleWrap.className = 'compass-needle';

  needleWrap.innerHTML = `
    <svg viewBox="0 0 64 64" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="32" cy="32" r="30" class="compass-ring" />
      <polygon points="32,6 28,30 32,26 36,30" class="compass-north" />
      <polygon points="32,58 28,34 32,38 36,34" class="compass-south" />
      <text x="32" y="15" class="compass-label compass-label-n" data-i18n="compass.north">${t('compass.north') || 'N'}</text>
      <text x="32" y="59" class="compass-label compass-label-s" data-i18n="compass.south">${t('compass.south') || 'S'}</text>
      <text x="6" y="35" class="compass-label compass-label-w" data-i18n="compass.west">${t('compass.west') || 'W'}</text>
      <text x="58" y="35" class="compass-label compass-label-e" data-i18n="compass.east">${t('compass.east') || 'E'}</text>
      <circle cx="32" cy="32" r="3" class="compass-center" />
    </svg>
  `;

  compassEl!.appendChild(needleWrap);

  compassEl!.addEventListener('pointerdown', onPointerDown);
  compassEl!.addEventListener('pointermove', onPointerMove);
  compassEl!.addEventListener('pointerup', onPointerUp);
  compassEl!.addEventListener('pointercancel', onPointerUp);
  compassEl!.addEventListener('dblclick', onDoubleClick);

  container.appendChild(compassEl!);
  updateCompassTransform();

  return compassEl;
}

export function destroyCompass() {
  if (!compassEl) return;
  compassEl!.removeEventListener('pointerdown', onPointerDown);
  compassEl!.removeEventListener('pointermove', onPointerMove);
  compassEl!.removeEventListener('pointerup', onPointerUp);
  compassEl!.removeEventListener('pointercancel', onPointerUp);
  compassEl!.removeEventListener('dblclick', onDoubleClick);
  compassEl!.remove();
  compassEl = null;
}

export function updateCompassLabels() {
  if (!compassEl) return;
  const svg = compassEl!.querySelector('svg');
  if (!svg) return;
  const n = svg.querySelector('.compass-label-n');
  const s = svg.querySelector('.compass-label-s');
  const e = svg.querySelector('.compass-label-e');
  const w = svg.querySelector('.compass-label-w');
  if (n) n.textContent = t('compass.north');
  if (s) s.textContent = t('compass.south');
  if (e) e.textContent = t('compass.east');
  if (w) w.textContent = t('compass.west');
  compassEl!.setAttribute('aria-label', t('compass.ariaLabel'));
  compassEl!.setAttribute('title', t('compass.tooltip'));
}
